import React, { useMemo, useState } from 'react';
import {
  Alert, Button, Card, Col, DatePicker, Empty, Row, Segmented, Space, Spin, Statistic, Table, Tag, Typography
} from 'antd';
import {
  ArrowDownOutlined, ArrowUpOutlined, CreditCardOutlined, DollarOutlined, FundOutlined,
  LineChartOutlined, PercentageOutlined, ReloadOutlined, ShoppingCartOutlined, WalletOutlined
} from '@ant-design/icons';
import { Column, Line, Pie } from '@ant-design/charts';
import { useQuery } from 'react-query';
import dayjs from 'dayjs';
import api from '../../utils/api';
import FinancialSummary from '../../components/FinancialSummary';
import useFinancialRefresh from '../../hooks/useFinancialRefresh';
import { useAuth } from '../../contexts/AuthContext';
import './DashboardWithQuote.css';

const { Title, Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;

const FILTERS = [
  { label: 'เดือนนี้', value: 'this_month' },
  { label: 'เดือนที่แล้ว', value: 'last_month' },
  { label: '3 เดือน', value: '3_months' },
  { label: '6 เดือน', value: '6_months' },
  { label: 'ปีนี้', value: 'this_year' },
  { label: 'ปีที่แล้ว', value: 'last_year' },
  { label: 'กำหนดเอง', value: 'custom' }
];

const getPeriod = key => {
  const now = dayjs();
  if (key === 'last_month') return [now.subtract(1, 'month').startOf('month'), now.subtract(1, 'month').endOf('month')];
  if (key === '3_months') return [now.subtract(2, 'month').startOf('month'), now.endOf('month')];
  if (key === '6_months') return [now.subtract(5, 'month').startOf('month'), now.endOf('month')];
  if (key === 'this_year') return [now.startOf('year'), now.endOf('year')];
  if (key === 'last_year') return [now.subtract(1, 'year').startOf('year'), now.subtract(1, 'year').endOf('year')];
  return [now.startOf('month'), now.endOf('month')];
};

const money = value => Number(value || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const compactMoney = value => new Intl.NumberFormat('th-TH', { notation: 'compact', maximumFractionDigits: 1 }).format(Number(value || 0));
const statusLabel = { paid: 'ชำระแล้ว', partial: 'ชำระบางส่วน', invoice: 'รอชำระ', overdue: 'เกินกำหนด', approved: 'อนุมัติ', pending: 'รอตรวจสอบ' };
const statusColor = { paid: 'success', partial: 'warning', invoice: 'processing', overdue: 'error', approved: 'success', pending: 'warning' };

const Growth = ({ value, inverse = false }) => {
  const number = Number(value || 0);
  const positive = inverse ? number <= 0 : number >= 0;
  return <Tag color={positive ? 'success' : 'error'} icon={number >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}>
    {Math.abs(number).toFixed(1)}%
  </Tag>;
};

const Dashboard = () => {
  const { user } = useAuth();
  const [filter, setFilter] = useState('this_month');
  const [period, setPeriod] = useState(getPeriod('this_month'));

  const changeFilter = value => {
    setFilter(value);
    if (value !== 'custom') setPeriod(getPeriod(value));
  };

  const queryParams = useMemo(() => ({
    start_date: period[0].format('YYYY-MM-DD'),
    end_date: period[1].format('YYYY-MM-DD')
  }), [period]);

  const { data: response, isLoading, isFetching, error, refetch } = useQuery(
    ['business-overview', queryParams.start_date, queryParams.end_date],
    () => api.get('/api/reports/business-overview', { params: queryParams }),
    { staleTime: 0, keepPreviousData: false }
  );

  useFinancialRefresh(refetch);
  const data = response?.data?.data || {};
  const summary = data.summary || {};
  const trend = data.trend || [];
  const expenseCategories = data.expense_categories || [];
  const topProducts = data.top_products || [];
  const recent = data.recent_activities || { receipts: [], expenses: [] };

  const trendData = useMemo(() => trend.flatMap(item => [
    { period: item.period, metric: 'รายได้', value: item.revenue },
    { period: item.period, metric: 'ค่าใช้จ่าย', value: item.expenses },
    ...(item.net_profit !== null ? [{ period: item.period, metric: 'กำไรสุทธิ (Posted)', value: item.net_profit }] : [])
  ]), [trend]);

  const costData = useMemo(() => trend.flatMap(item => [
    { period: item.period, metric: 'ต้นทุนขาย', value: item.cogs },
    { period: item.period, metric: 'ค่าใช้จ่ายดำเนินงาน (Posted)', value: item.operating_expenses }
  ]).filter(item => item.value !== null), [trend]);

  const kpis = [
    { title: 'รายรับรวม VAT', value: summary.revenue, growth: summary.growth?.revenue, color: 'green', icon: <DollarOutlined /> },
    { title: 'รายจ่ายรวม VAT', value: summary.expenses, growth: summary.growth?.expenses, inverse: true, color: 'red', icon: <CreditCardOutlined /> },
    { title: 'ต้นทุนขาย (Posted)', value: summary.cogs, growth: summary.growth?.cogs, inverse: true, color: 'orange', icon: <ShoppingCartOutlined /> },
    { title: 'กำไรขั้นต้น (Posted)', value: summary.gross_profit, growth: summary.growth?.gross_profit, color: 'blue', icon: <FundOutlined /> },
    { title: 'กำไรสุทธิ (Posted)', value: summary.net_profit, growth: summary.growth?.net_profit, color: Number(summary.net_profit) >= 0 ? 'purple' : 'red', icon: <LineChartOutlined /> },
    { title: 'ลูกหนี้คงค้าง', value: summary.outstanding, subtitle: `${summary.outstanding_invoices || 0} เอกสาร`, color: 'gold', icon: <WalletOutlined /> }
  ];

  const insightItems = [
    {
      title: Number(summary.net_profit) >= 0 ? 'ธุรกิจมีกำไรในช่วงนี้' : 'ธุรกิจขาดทุนในช่วงนี้',
      description: `อัตรากำไรสุทธิ ${Number(summary.net_margin || 0).toFixed(1)}% จากรายได้ทั้งหมด`,
      type: Number(summary.net_profit) >= 0 ? 'success' : 'error'
    },
    {
      title: 'ประสิทธิภาพกำไรขั้นต้น',
      description: `Gross margin ${Number(summary.gross_margin || 0).toFixed(1)}% หลังหักต้นทุนขาย`,
      type: Number(summary.gross_margin) >= 30 ? 'success' : 'warning'
    },
    {
      title: topProducts[0] ? `สินค้าทำรายได้สูงสุด: ${topProducts[0].product_name}` : 'ยังไม่มีข้อมูลสินค้าขาย',
      description: topProducts[0] ? `รายได้ ${money(topProducts[0].revenue)} บาท ยอดรวม VAT ${money(topProducts[0].total_amount)} บาท` : 'เลือกช่วงเวลาอื่นเพื่อวิเคราะห์เพิ่มเติม',
      type: 'info'
    }
  ];

  const lineConfig = {
    data: trendData, xField: 'period', yField: 'value', colorField: 'metric', height: 310,
    scale: { color: { range: ['#16a34a', '#ef4444', '#7c3aed'] } },
    axis: { y: { labelFormatter: compactMoney }, x: { labelAutoRotate: true } },
    tooltip: { items: [{ channel: 'y', valueFormatter: value => `${money(value)} บาท` }] },
    style: { lineWidth: 3 }, point: { size: 3 }, interaction: { tooltip: { shared: true } }
  };
  const costConfig = {
    data: costData, xField: 'period', yField: 'value', colorField: 'metric', height: 270,
    transform: [{ type: 'dodgeX' }], scale: { color: { range: ['#f59e0b', '#ef4444'] } },
    axis: { y: { labelFormatter: compactMoney } }, tooltip: { items: [{ channel: 'y', valueFormatter: value => `${money(value)} บาท` }] }
  };
  const pieConfig = {
    data: expenseCategories, angleField: 'amount', colorField: 'category', height: 270,
    innerRadius: 0.58, label: { text: 'category', position: 'outside' },
    legend: { color: { position: 'bottom', layout: { justifyContent: 'center' } } },
    tooltip: { items: [{ channel: 'y', valueFormatter: value => `${money(value)} บาท` }] }
  };
  const productConfig = {
    data: topProducts, xField: 'product_name', yField: 'revenue', height: 300,
    colorField: 'product_name', legend: false, axis: { y: { labelFormatter: compactMoney }, x: { labelAutoRotate: true } },
    tooltip: { items: [{ channel: 'y', valueFormatter: value => `${money(value)} บาท` }] }
  };

  const receiptColumns = [
    { title: 'เลขที่', dataIndex: 'invoice_number', render: (value, row) => value || row.receipt_number || '-' },
    { title: 'ลูกค้า', dataIndex: 'customer_name', ellipsis: true },
    { title: 'ยอดรวม', dataIndex: 'total_amount', align: 'right', render: value => money(value) },
    { title: 'สถานะ', dataIndex: 'status', render: value => <Tag color={statusColor[value]}>{statusLabel[value] || value}</Tag> }
  ];
  const expenseColumns = [
    { title: 'เลขที่', dataIndex: 'expense_number' },
    { title: 'รายการ', dataIndex: 'description', ellipsis: true },
    { title: 'ยอดรวม', dataIndex: 'total_amount', align: 'right', render: value => money(value) },
    { title: 'สถานะ', dataIndex: 'status', render: value => <Tag color={statusColor[value]}>{statusLabel[value] || value}</Tag> }
  ];

  return <div className="business-dashboard">
    <div className="dashboard-topbar">
      <div><Title level={2}>ภาพรวมธุรกิจ</Title><Text type="secondary">สวัสดี {user?.name} · วิเคราะห์ผลประกอบการจากข้อมูลจริงในระบบ</Text></div>
      <Button icon={<ReloadOutlined spin={isFetching} />} onClick={() => refetch()}>รีเฟรช</Button>
    </div>

    <Card className="dashboard-filter-card" bordered={false}>
      <Segmented block options={FILTERS} value={filter} onChange={changeFilter} />
      {filter === 'custom' && <RangePicker value={period} allowClear={false} format="DD/MM/YYYY" onChange={value => value && setPeriod(value)} />}
      <Text type="secondary">{period[0].format('DD MMM YYYY')} - {period[1].format('DD MMM YYYY')}</Text>
    </Card>

    {error && <Alert type="error" showIcon message="โหลดข้อมูล Dashboard ไม่สำเร็จ" description={error.response?.data?.message || error.message} />}
    {isLoading ? <div className="dashboard-loading"><Spin size="large" /></div> : <>
      <FinancialSummary data={data} />
      <Row gutter={[14, 14]} className="kpi-grid">
        {kpis.filter(kpi => kpi.value !== null).map(kpi => <Col xs={12} md={8} xl={4} key={kpi.title}>
          <Card className={`business-kpi ${kpi.color}`} bordered={false}>
            <div className="kpi-heading"><span className="kpi-icon">{kpi.icon}</span><Text>{kpi.title}</Text></div>
            <Statistic value={Number(kpi.value || 0)} precision={2} suffix="฿" />
            <div className="kpi-footer">{kpi.growth !== undefined ? <><Growth value={kpi.growth} inverse={kpi.inverse} /><Text type="secondary">เทียบช่วงก่อน</Text></> : <Text type="secondary">{kpi.subtitle}</Text>}</div>
          </Card>
        </Col>)}
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={16}><Card title="แนวโน้มรายได้ ค่าใช้จ่าย และกำไรสุทธิ" bordered={false}>{trendData.length ? <Line {...lineConfig} /> : <Empty />}</Card></Col>
        <Col xs={24} xl={8}><Card title="Business Insights" bordered={false} className="insight-card">
          <Space direction="vertical" size={12} style={{ width: '100%' }}>{(data.ledger ? insightItems : []).map(item => <Alert key={item.title} type={item.type} showIcon message={item.title} description={item.description} />)}</Space>
          <div className="margin-summary"><PercentageOutlined /><div><Text type="secondary">ยอดขายเฉลี่ยต่อรายการ</Text><Title level={4}>{money(summary.average_transaction)} ฿</Title></div></div>
        </Card></Col>
        <Col xs={24} lg={12} style={{ display: data.ledger ? undefined : 'none' }}><Card title="ต้นทุนขายเทียบค่าใช้จ่าย (Posted)" bordered={false}>{costData.length ? <Column {...costConfig} /> : <Empty />}</Card></Col>
        <Col xs={24} lg={12}><Card title="สัดส่วนค่าใช้จ่ายตามหมวดหมู่" bordered={false}>{expenseCategories.length ? <Pie {...pieConfig} /> : <Empty />}</Card></Col>
        <Col xs={24}><Card title="สินค้าหรือบริการที่สร้างรายได้สูงสุด" bordered={false}>{topProducts.length ? <Column {...productConfig} /> : <Empty />}</Card></Col>
        <Col xs={24} xl={12}><Card title="ใบเสร็จ/ใบแจ้งหนี้ล่าสุด" extra={<Button type="link" href="/receipts">ดูทั้งหมด</Button>} bordered={false}><Table size="small" rowKey="id" columns={receiptColumns} dataSource={recent.receipts || []} pagination={false} scroll={{ x: 560 }} /></Card></Col>
        <Col xs={24} xl={12}><Card title="ค่าใช้จ่ายล่าสุด" extra={<Button type="link" href="/expenses">ดูทั้งหมด</Button>} bordered={false}><Table size="small" rowKey="id" columns={expenseColumns} dataSource={recent.expenses || []} pagination={false} scroll={{ x: 560 }} /></Card></Col>
      </Row>
    </>}
    <Paragraph className="dashboard-footnote">ยอดเอกสารรวม VAT และรายการรอตรวจ · กำไรสุทธิและต้นทุนขายจากสมุดรายวันที่ Posted · ส่วนต่างรวม VAT แสดงแยกจากกำไรบัญชี</Paragraph>
  </div>;
};

export default Dashboard;
